import { numberToCurrency } from "utils/listings/display";
import i18n from "../../../locales/i18n";
import {
  includeBsadInput,
  mainTemplateColorInput,
  mainListingInput,
  realtorNameTemplateInput,
  realtorPhoneNumberTemplateInput,
  realtorImageTemplateInput,
  includeRealtorImageInput,
  realtorLogoTemplateInput,
  firstImageOfListingTemplateInput,
  listingPrice,
  getBsadPrompt,
  getRealtorImagePrompt,
  getVerticalFooterPrompt,
  getVisualStylePrompt,
  getColorInfo,
  getInputImageLabel,
  hasRealtorImage,
  promptGenerationFinalParams,
  templateDataType,
  promptGeneratorInitialParams,
  templateInput
} from "../template_sections";

const secondListingInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.hotPropertiesDuo.secondListing"),
  type: "listing",
  nameInPrompt: "second_listing",
  showInSection: "listing_info",
  description: i18n.t("creativesGenerator.templates.hotPropertiesDuo.secondListingDescription")
};

const secondListingImageInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.hotPropertiesDuo.secondListingImage"),
  type: "image",
  nameInPrompt: "second_listing_image",
  showInSection: "listing_info",
  description: i18n.t(
    "creativesGenerator.templates.hotPropertiesDuo.secondListingImageDescription"
  ),
  additionalData: {
    imageSourceHint: "listing_images",
    listingNameInPrompt: "second_listing"
  },
  defaultValue: (params: promptGeneratorInitialParams) => {
    const secondListing = params.selectedListings["second_listing"];
    return secondListing?.imageUrls?.length! > 0 ? secondListing?.imageUrls?.[0] : "";
  }
};

const secondListingPriceInput: templateInput = {
  label: i18n.t("creativesGenerator.templates.hotPropertiesDuo.secondListingPrice"),
  type: "text",
  nameInPrompt: "second_listing_price",
  showInSection: "listing_info",
  description: i18n.t(
    "creativesGenerator.templates.hotPropertiesDuo.secondListingPriceDescription"
  ),
  defaultValue: (params: promptGeneratorInitialParams) => {
    const secondListing = params.selectedListings["second_listing"];
    return secondListing?.price ? numberToCurrency(secondListing.price) : "_____ ₪";
  }
};

export const hot_properties_duo_var_1: templateDataType = {
  templateName: i18n.t("creativesGenerator.templates.hotPropertiesDuo.templateName"),
  displayImageUrl: "/static/creative-template-display-images/real-estate-hot-properties-duo-1.jpg",
  description: i18n.t("creativesGenerator.templates.hotPropertiesDuo.description"),
  inputs: [
    includeBsadInput,
    mainTemplateColorInput,
    // First listing
    mainListingInput,
    firstImageOfListingTemplateInput,
    listingPrice,
    {
      label: i18n.t("creativesGenerator.templates.hotPropertiesDuo.firstListingAddress"),
      type: "text",
      nameInPrompt: "first_listing_address",
      showInSection: "template_specific",
      description: i18n.t(
        "creativesGenerator.templates.hotPropertiesDuo.firstListingAddressDescription"
      ),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        const street = mainListing?.address?.street ?? "_____";
        const houseNumber = mainListing?.address?.houseNumber ?? "";
        const neighborhood = mainListing?.address?.neighborhood ?? "_____";
        return `${street} ${houseNumber}, ${neighborhood}`;
      }
    },
    {
      label: i18n.t("creativesGenerator.templates.hotPropertiesDuo.firstListingDetails"),
      type: "text",
      nameInPrompt: "first_listing_details",
      showInSection: "template_specific",
      description: i18n.t(
        "creativesGenerator.templates.hotPropertiesDuo.firstListingDetailsDescription"
      ),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        const rooms = mainListing?.roomsCount ?? "____";
        return i18n.t(
          "creativesGenerator.templates.hotPropertiesDuo.firstListingDetailsDefaultValue",
          { roomsCount: rooms }
        );
      }
    },
    // Second listing
    secondListingInput,
    secondListingImageInput,
    secondListingPriceInput,
    {
      label: i18n.t("creativesGenerator.templates.hotPropertiesDuo.secondListingAddress"),
      type: "text",
      nameInPrompt: "second_listing_address",
      showInSection: "template_specific",
      description: i18n.t(
        "creativesGenerator.templates.hotPropertiesDuo.secondListingAddressDescription"
      ),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const secondListing = params.selectedListings["second_listing"];
        const street = secondListing?.address?.street ?? "_____";
        const houseNumber = secondListing?.address?.houseNumber ?? "";
        const neighborhood = secondListing?.address?.neighborhood ?? "_____";
        return `${street} ${houseNumber}, ${neighborhood}`;
      }
    },
    {
      label: i18n.t("creativesGenerator.templates.hotPropertiesDuo.secondListingDetails"),
      type: "text",
      nameInPrompt: "second_listing_details",
      showInSection: "template_specific",
      description: i18n.t(
        "creativesGenerator.templates.hotPropertiesDuo.secondListingDetailsDescription"
      ),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const secondListing = params.selectedListings["second_listing"];
        const rooms = secondListing?.roomsCount ?? "____";
        return i18n.t(
          "creativesGenerator.templates.hotPropertiesDuo.secondListingDetailsDefaultValue",
          { roomsCount: rooms }
        );
      }
    },
    // Title text
    {
      label: i18n.t("creativesGenerator.templates.hotPropertiesDuo.mainTitle"),
      type: "text",
      nameInPrompt: "main_title",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.hotPropertiesDuo.mainTitleDescription"),
      defaultValue: () =>
        i18n.t("creativesGenerator.templates.hotPropertiesDuo.mainTitleDefaultValue")
    },
    {
      label: i18n.t("creativesGenerator.templates.hotPropertiesDuo.subtitle"),
      type: "text",
      nameInPrompt: "subtitle_text",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.hotPropertiesDuo.subtitleDescription"),
      defaultValue: () =>
        i18n.t("creativesGenerator.templates.hotPropertiesDuo.subtitleDefaultValue")
    },
    // Realtor info
    realtorNameTemplateInput,
    realtorPhoneNumberTemplateInput,
    realtorImageTemplateInput,
    includeRealtorImageInput,
    realtorLogoTemplateInput
  ],
  promptGenerator: (params: promptGenerationFinalParams) => {
    const { mainColorValue, mainColorName, lighterColorValue, darkerColorValue } =
      getColorInfo(params);
    const textParams = params.templateFinalInputs.filter((input) => input.type === "text");

    const getTextValue = (nameInPrompt: string) =>
      textParams.find((param) => param.nameInPrompt === nameInPrompt)?.finalValue ?? "";

    return `
{
  "description": "Hot Properties Duo - Two Listings Showcase Flyer",
  "project_context": {
    "type": "Real Estate Social Media Graphic",
    "tone": "Professional, luxurious, bold, and eye-catching",
    "aspect_ratio": "4:5 (Portrait, optimized for Instagram/Facebook)"
  },
  ${getVisualStylePrompt(params)},
  ${getBsadPrompt(params)}

  "layout": {
    "type": "two_column_asymmetric",
    "background": {
      "color": "${darkerColorValue}",
      "style": "Deep ${mainColorName} background with subtle dark city skyline silhouette in the background. Add subtle gold sparkle/particle effects scattered across the design for a premium luxurious feel."
    },
    "left_column": {
      "width": "55%",
      "content": [
        {
          "type": "listing_card",
          "position": "top",
          "height": "45%",
          "image": {
            "source": "listing_image",
            "style": "rounded corners (10px), white border frame (3px), object_fit: cover",
            "size": "fills the card area"
          },
          "price_tag": {
            "text": "${getTextValue("listing_price")}",
            "position": "top-right of the image, slightly overlapping the edge",
            "style": "red background (#e53935), white bold text, rounded corners, padding 6px 14px, font_size: medium"
          },
          "address_bar": {
            "text": "${getTextValue("first_listing_address")}",
            "position": "below image",
            "style": "bold white Hebrew text on a semi-transparent dark-gold gradient banner, font_size: large, text-align: right (RTL)"
          },
          "details_bar": {
            "text": "${getTextValue("first_listing_details")}",
            "position": "below address bar",
            "style": "white text on dark background strip, font_size: small-medium, text-align: right (RTL)"
          }
        },
        {
          "type": "listing_card",
          "position": "bottom",
          "height": "45%",
          "image": {
            "source": "second_listing_image",
            "style": "rounded corners (10px), white border frame (3px), object_fit: cover",
            "size": "fills the card area"
          },
          "price_tag": {
            "text": "${getTextValue("second_listing_price")}",
            "position": "top-right of the image, slightly overlapping the edge",
            "style": "red background (#e53935), white bold text, rounded corners, padding 6px 14px, font_size: medium"
          },
          "address_bar": {
            "text": "${getTextValue("second_listing_address")}",
            "position": "below image",
            "style": "bold white Hebrew text on a semi-transparent dark-gold gradient banner, font_size: large, text-align: right (RTL)"
          },
          "details_bar": {
            "text": "${getTextValue("second_listing_details")}",
            "position": "below address bar",
            "style": "white text on dark background strip, font_size: small-medium, text-align: right (RTL)"
          }
        }
      ]
    },
    "right_column": {
      "width": "45%",
      "content": [
        {
          "type": "hero_title",
          "text": "${getTextValue("main_title")}",
          "position": "top-right area",
          "style": "extra-large bold gold/cream colored Hebrew text, dramatic and attention-grabbing, stacked vertically line by line, right-aligned (RTL). Make this the dominant visual element on the right side."
        },
        {
          "type": "subtitle",
          "text": "${getTextValue("subtitle_text")}",
          "position": "below hero title",
          "style": "handwritten/script style white Hebrew text, elegant cursive font, medium size"
        }
      ]
    }
  },
  ${
    hasRealtorImage(params)
      ? `${getRealtorImagePrompt(
          params,
          "The realtor should be placed on the right side, overlapping between the main content area and the footer. Medium-large size, professional cut-out with no background."
        )},`
      : ""
  }
  ${getVerticalFooterPrompt(params)},
  "input_images": {
    "listing_image": "${getInputImageLabel("listing_image")}",
    "second_listing_image": "${getInputImageLabel("second_listing_image")}",
    ${hasRealtorImage(params) ? `"realtor_image": "${getInputImageLabel("realtor_image")}",` : ""}
    "realtor_logo": "${getInputImageLabel("realtor_logo")}"
  },
  "important_notes": [
    "This is an RTL (right-to-left) Hebrew design - all text must be right-aligned",
    "The two listing cards should be stacked vertically on the left side, each with its own property image, red price tag, address bar, and details bar",
    "The hero title on the right should be very large, bold, and gold/cream colored - it is the main visual anchor",
    "Add subtle gold sparkle/particle effects for a premium luxury feel",
    "The background should have a subtle dark city skyline silhouette for depth",
    "Each property image should have a white border frame with rounded corners",
    "Price tags should be in red (#e53935) with white text, positioned at the top-right of each image",
    "DO NOT alter the provided property images beyond resizing/cropping as necessary for composition",
    "Maintain clear visual separation between the two listing cards"
  ]
}`;
  }
};
